import { toast } from 'react-toastify';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
  GET_ROLESANDPERMISSIONS,
  GET_SUPPLIERROLE,
  FETCH_PARENT_ROLES,
  FETCH_PERMISSIONS_TREE,
  FETCH_ROLE_PERMISSIONS,
  UPDATE_ROLE_PERMISSIONS,
  FETCH_SUB_ROLES,
  FETCH_FILTERED_TREE,
  FETCH_SUB_ROLE_PERMS,
  CREATE_SUB_ROLE,
  UPDATE_SUB_ROLE,
} from 'resources/actionTypes';
import {
  getRolesAndPermissionsService,
  getSupplierRolesApi,
  getParentRolesService,
  getFullPermissionsTreeService,
  getParentAssignedPermissionsService,
  updateParentPermissionsService,
  getSubRolesService,
  getFilteredPermissionsTreeService,
  getSubRoleAssignedPermissionsService,
  createSubRoleService,
  updateSubRoleService,
} from 'services/RolesAndPermissionsService';
import { isSuperAdmin } from 'utils/jwtRole';

// Existing saga function
function* getRolesAndPermissions(action) {
  const payload = action.payload || '';
  const rolesAndPermissions = yield call(getRolesAndPermissionsService);

  const roles = rolesAndPermissions?.data?.map((arrayValue) => arrayValue?.moduleKey);

  if (rolesAndPermissions.success) {
    if (payload) {
      const subRoles = rolesAndPermissions?.data?.map((parentModule) =>
        parentModule?.subModule?.map((arrayValue) => arrayValue?.moduleKey),
      );

      const dmmyArray = [...roles, ...subRoles.flat(), 'settings', 'logout'];
      localStorage?.setItem('rolesPermissions', JSON.stringify(dmmyArray));

      // Multi-tenancy: the super admin (JWT role 99) lands on the borrow-a-
      // seat picker instead of a tenant module.
      if (isSuperAdmin()) {
        window.location.replace('/select-tenant');
      } else {
        window.location.replace(`/${dmmyArray[0]}`);
      }
    }

    yield put({
      type: GET_ROLESANDPERMISSIONS.GET_ROLESANDPERMISSIONS_SUCCESS,
      rolesAndPermissions: rolesAndPermissions?.data,
    });
  } else {
    yield put({
      type: GET_ROLESANDPERMISSIONS.GET_ROLESANDPERMISSIONS_FAILED,
      message: rolesAndPermissions.message,
    });
    toast.error(rolesAndPermissions?.message || 'Fetching roles and permissions failed');
  }
}

export function* getRolesAndPermissionsWatcher() {
  yield takeEvery(
    GET_ROLESANDPERMISSIONS.GET_ROLESANDPERMISSIONS_REQUESTED,
    getRolesAndPermissions,
  );
}

// Existing saga function
function* getSupplierRoles(action) {
  const payload = action.payload || {};
  const supplierRoles = yield call(getSupplierRolesApi, payload);

  if (supplierRoles.success) {
    yield put({
      type: GET_SUPPLIERROLE.GET_SUPPLIERROLE_SUCCESS,
      supplierRoles: supplierRoles?.data,
    });
  } else {
    yield put({
      type: GET_SUPPLIERROLE.GET_SUPPLIERROLE_FAILED,
      message: supplierRoles.message,
    });
    toast.error(supplierRoles?.message || 'Fetching roles failed');
  }
}

export function* getSupplierRoleWatcher() {
  yield takeEvery(GET_SUPPLIERROLE.GET_SUPPLIERROLE_REQUESTED, getSupplierRoles);
}

// New sagas for RBAC
function* fetchParentRolesSaga(action) {
  const res = yield call(getParentRolesService);
  if (res?.success) {
    yield put({ type: FETCH_PARENT_ROLES.FETCH_PARENT_ROLES_SUCCESS, payload: res.data });
  } else {
    yield put({ type: FETCH_PARENT_ROLES.FETCH_PARENT_ROLES_FAILED, payload: res?.message });
    toast.error(res?.message || 'Failed to fetch parent roles');
  }
  if (action.resolve) action.resolve(res);
}

export function* fetchParentRolesWatcher() {
  yield takeEvery(FETCH_PARENT_ROLES.FETCH_PARENT_ROLES_REQUESTED, fetchParentRolesSaga);
}

function* fetchPermissionsTreeSaga(action) {
  const res = yield call(getFullPermissionsTreeService);
  if (res?.success) {
    yield put({ type: FETCH_PERMISSIONS_TREE.FETCH_PERMISSIONS_TREE_SUCCESS, payload: res.data });
  } else {
    yield put({
      type: FETCH_PERMISSIONS_TREE.FETCH_PERMISSIONS_TREE_FAILED,
      payload: res?.message,
    });
    toast.error(res?.message || 'Failed to fetch permissions tree');
  }
  if (action.resolve) action.resolve(res);
}

export function* fetchPermissionsTreeWatcher() {
  yield takeEvery(
    FETCH_PERMISSIONS_TREE.FETCH_PERMISSIONS_TREE_REQUESTED,
    fetchPermissionsTreeSaga,
  );
}

function* fetchRolePermissionsSaga(action) {
  const res = yield call(getParentAssignedPermissionsService, action.payload.parentRoleId);
  if (res?.success) {
    yield put({ type: FETCH_ROLE_PERMISSIONS.FETCH_ROLE_PERMISSIONS_SUCCESS, payload: res.data });
  } else {
    yield put({
      type: FETCH_ROLE_PERMISSIONS.FETCH_ROLE_PERMISSIONS_FAILED,
      payload: res?.message,
    });
    toast.error(res?.message || 'Failed to fetch role permissions');
  }
  if (action.resolve) action.resolve(res);
}

export function* fetchRolePermissionsWatcher() {
  yield takeEvery(
    FETCH_ROLE_PERMISSIONS.FETCH_ROLE_PERMISSIONS_REQUESTED,
    fetchRolePermissionsSaga,
  );
}

function* updateRolePermissionsSaga(action) {
  const res = yield call(
    updateParentPermissionsService,
    action.payload.parentRoleId,
    action.payload.permissionIds,
  );
  if (res?.success) {
    yield put({ type: UPDATE_ROLE_PERMISSIONS.UPDATE_ROLE_PERMISSIONS_SUCCESS, payload: res.data });
    // QA #62: refresh permissions so the menu/UI updates without a manual reload
    yield put({ type: GET_ROLESANDPERMISSIONS.GET_ROLESANDPERMISSIONS_REQUESTED });
  } else {
    yield put({
      type: UPDATE_ROLE_PERMISSIONS.UPDATE_ROLE_PERMISSIONS_FAILED,
      payload: res?.message,
    });
    toast.error(res?.message || 'Failed to update role permissions');
  }
  if (action.resolve) action.resolve(res);
}

export function* updateRolePermissionsWatcher() {
  yield takeEvery(
    UPDATE_ROLE_PERMISSIONS.UPDATE_ROLE_PERMISSIONS_REQUESTED,
    updateRolePermissionsSaga,
  );
}

function* fetchSubRolesSaga(action) {
  const res = yield call(getSubRolesService, action.payload.parentRoleId);
  if (res?.success) {
    yield put({ type: FETCH_SUB_ROLES.FETCH_SUB_ROLES_SUCCESS, payload: res.data });
  } else {
    yield put({ type: FETCH_SUB_ROLES.FETCH_SUB_ROLES_FAILED, payload: res?.message });
    toast.error(res?.message || 'Failed to fetch sub roles');
  }
  if (action.resolve) action.resolve(res);
}

export function* fetchSubRolesWatcher() {
  yield takeEvery(FETCH_SUB_ROLES.FETCH_SUB_ROLES_REQUESTED, fetchSubRolesSaga);
}

function* fetchFilteredTreeSaga(action) {
  const res = yield call(getFilteredPermissionsTreeService, action.payload.parentRoleId);
  if (res?.success) {
    // API returns { data: { role: {...}, modules: [...] } } — extract modules array
    const modules = res.data?.modules || res.data || [];
    yield put({ type: FETCH_FILTERED_TREE.FETCH_FILTERED_TREE_SUCCESS, payload: modules });
  } else {
    yield put({ type: FETCH_FILTERED_TREE.FETCH_FILTERED_TREE_FAILED, payload: res?.message });
    toast.error(res?.message || 'Failed to fetch filtered permissions tree');
  }
  if (action.resolve) action.resolve(res);
}

export function* fetchFilteredTreeWatcher() {
  yield takeEvery(FETCH_FILTERED_TREE.FETCH_FILTERED_TREE_REQUESTED, fetchFilteredTreeSaga);
}

function* fetchSubRolePermsSaga(action) {
  const res = yield call(getSubRoleAssignedPermissionsService, action.payload.subRoleId);
  if (res?.success) {
    yield put({ type: FETCH_SUB_ROLE_PERMS.FETCH_SUB_ROLE_PERMS_SUCCESS, payload: res.data });
  } else {
    yield put({ type: FETCH_SUB_ROLE_PERMS.FETCH_SUB_ROLE_PERMS_FAILED, payload: res?.message });
    toast.error(res?.message || 'Failed to fetch sub-role permissions');
  }
  if (action.resolve) action.resolve(res);
}

export function* fetchSubRolePermsWatcher() {
  yield takeEvery(FETCH_SUB_ROLE_PERMS.FETCH_SUB_ROLE_PERMS_REQUESTED, fetchSubRolePermsSaga);
}

// create sub role
function* createSubRoleSaga(action) {
  const res = yield call(createSubRoleService, action.payload);
  if (res?.success) {
    yield put({ type: CREATE_SUB_ROLE.CREATE_SUB_ROLE_SUCCESS, payload: res.data });
  } else {
    yield put({ type: CREATE_SUB_ROLE.CREATE_SUB_ROLE_FAILED, payload: res?.message });
    toast.error(res?.message || 'Failed to create sub-role');
  }
  if (action.resolve) action.resolve(res);
}

export function* createSubRoleWatcher() {
  yield takeEvery(CREATE_SUB_ROLE.CREATE_SUB_ROLE_REQUESTED, createSubRoleSaga);
}

// update sub role
function* updateSubRoleSaga(action) {
  const { subRoleId, ...payload } = action.payload;
  const res = yield call(updateSubRoleService, subRoleId, payload);
  if (res?.success) {
    yield put({ type: UPDATE_SUB_ROLE.UPDATE_SUB_ROLE_SUCCESS, payload: res.data });
    // QA #62: refresh permissions so the menu/UI updates without a manual reload
    yield put({ type: GET_ROLESANDPERMISSIONS.GET_ROLESANDPERMISSIONS_REQUESTED });
  } else {
    yield put({ type: UPDATE_SUB_ROLE.UPDATE_SUB_ROLE_FAILED, payload: res?.message });
    toast.error(res?.message || 'Failed to update sub-role');
  }
  if (action.resolve) action.resolve(res);
}

export function* updateSubRoleWatcher() {
  yield takeEvery(UPDATE_SUB_ROLE.UPDATE_SUB_ROLE_REQUESTED, updateSubRoleSaga);
}
